import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Briefcase, Globe, Cake, CalendarDays, Pencil, Check, X } from 'lucide-react';
import { GENDER_OPTIONS, TIMEZONE_OPTIONS } from './constants';

const Field = ({ label, icon: Icon, value, empty = 'Not set' }) => (
  <div className="flex items-start gap-3">
    {Icon && (
      <div className="w-8 h-8 rounded-lg bg-orange-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={14} className="text-orange-primary" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-charcoal/50 text-xs uppercase tracking-wider">{label}</div>
      <div className={`text-sm ${value ? 'text-charcoal' : 'text-charcoal/30'}`}>
        {value || empty}
      </div>
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const BasicInfoSection = ({
  profile,
  organisation,
  displayName,
  email,
  readOnly = false,
  hidePrivate = false,
  onSave,   // async (payload) => void
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (profile && !editing) {
      setDraft({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        position: profile.position || '',
        timezone: profile.timezone || '',
        date_of_birth: profile.date_of_birth || '',
        joined_org_on: profile.joined_org_on || '',
        gender: profile.gender || '',
      });
    }
  }, [profile, editing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Send empty strings as null for cleanliness
      const payload = Object.fromEntries(
        Object.entries(draft).map(([k, v]) => [k, v === '' ? null : v])
      );
      await onSave(payload);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const fullName = [profile?.first_name, profile?.last_name]
    .filter(Boolean).join(' ') || displayName || '—';

  return (
    <section
      className="rounded-3xl bg-white/40 border border-charcoal/5 p-5 sm:p-6"
      data-testid="basic-info-section"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-3xl text-charcoal break-words">
            {fullName}
          </h1>
          {profile?.position && (
            <p className="text-sm text-charcoal/60 mt-1">{profile.position}</p>
          )}
          {organisation && (
            <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full bg-orange-primary/10 text-orange-primary text-xs">
              {organisation}
            </span>
          )}
          {email && (
            <div className="text-xs text-charcoal/40 mt-2">{email}</div>
          )}
        </div>

        {!readOnly && (
          <button
            type="button"
            onClick={() => setEditing(e => !e)}
            data-testid="edit-basic-info"
            className="p-2 rounded-full hover:bg-orange-primary/10 transition-colors flex-shrink-0"
            aria-label={editing ? 'Close edit' : 'Edit basic info'}
          >
            {editing
              ? <X size={16} className="text-charcoal/60" />
              : <Pencil size={16} className="text-charcoal/60" />}
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {editing && !readOnly ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            <Input label="First name" testId="input-first-name"
              value={draft.first_name}
              onChange={v => setDraft(d => ({ ...d, first_name: v }))} />
            <Input label="Last name" testId="input-last-name"
              value={draft.last_name}
              onChange={v => setDraft(d => ({ ...d, last_name: v }))} />
            <Input label="Position" testId="input-position"
              value={draft.position}
              onChange={v => setDraft(d => ({ ...d, position: v }))} />
            <Select label="Timezone" testId="input-timezone"
              value={draft.timezone} options={TIMEZONE_OPTIONS}
              onChange={v => setDraft(d => ({ ...d, timezone: v }))} />
            <Input type="date" label="Date of birth" testId="input-dob"
              value={draft.date_of_birth}
              onChange={v => setDraft(d => ({ ...d, date_of_birth: v }))} />
            <Input type="date" label={`Joined ${organisation} on`} testId="input-joined-on"
              value={draft.joined_org_on}
              onChange={v => setDraft(d => ({ ...d, joined_org_on: v }))} />
            <Select label="Gender" testId="input-gender"
              value={draft.gender} options={GENDER_OPTIONS}
              onChange={v => setDraft(d => ({ ...d, gender: v }))} />

            <div className="sm:col-span-2 flex items-center gap-2 pt-2">
              <button type="button" disabled={saving} onClick={handleSave}
                data-testid="save-basic-info"
                className="px-4 py-1.5 rounded-full bg-orange-primary text-white text-sm font-medium hover:bg-orange-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                <Check size={14} />{saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" disabled={saving} onClick={() => setEditing(false)}
                data-testid="cancel-basic-info"
                className="px-4 py-1.5 rounded-full bg-charcoal/5 text-charcoal/70 text-sm hover:bg-charcoal/10 transition-colors">
                Cancel
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <Field label="Timezone" icon={Globe} value={profile?.timezone} />
            <Field label="Position" icon={Briefcase} value={profile?.position} />
            <Field label={`Joined ${organisation || 'organisation'} on`}
              icon={CalendarDays}
              value={profile?.joined_org_on ? formatDate(profile.joined_org_on) : null} />
            {!hidePrivate && (
              <>
                <Field label="Date of birth" icon={Cake}
                  value={profile?.date_of_birth ? formatDate(profile.date_of_birth) : null} />
                <Field label="Gender" icon={UserIcon} value={profile?.gender} />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Input = ({ label, value, onChange, type = 'text', testId }) => (
  <label className="block">
    <span className="block text-charcoal/50 text-xs uppercase tracking-wider mb-1">{label}</span>
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      data-testid={testId}
      className="w-full px-3 py-2 rounded-lg border border-charcoal/10 bg-white text-sm text-charcoal focus:outline-none focus:border-orange-primary/50 transition-colors"
    />
  </label>
);

const Select = ({ label, value, onChange, options = [], testId }) => (
  <label className="block">
    <span className="block text-charcoal/50 text-xs uppercase tracking-wider mb-1">{label}</span>
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      data-testid={testId}
      className="w-full px-3 py-2 rounded-lg border border-charcoal/10 bg-white text-sm text-charcoal focus:outline-none focus:border-orange-primary/50 transition-colors"
    >
      <option value="">Select…</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export default BasicInfoSection;
